import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Calendar, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const CalendarConnectionBanner = () => {
  const { t } = useTranslation();
  const { user, isClient, isImpersonating } = useAuth();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const needsConnection = !!user
    && !isClient
    && !isImpersonating
    && ['admin', 'manager', 'agent'].includes(user.role)
    && user.calendarConnected === false;

  // Daily 8am modal — once per local day when calendar is missing
  useEffect(() => {
    if (!needsConnection) return;
    const hour = new Date().getHours();
    if (hour < 8) return;
    const storageKey = `calendarConnectionModalShown:${user.id}`;
    if (localStorage.getItem(storageKey) === todayKey()) return;
    setShowModal(true);
    localStorage.setItem(storageKey, todayKey());
  }, [needsConnection, user]);

  if (!needsConnection) return null;
  if (location.pathname.startsWith('/profile')) return null;

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/calendar/oauth/connect');
      window.location.href = data.url;
    } catch {
      toast.error(t('profile.gcalConnectError'));
      setConnecting(false);
    }
  };

  return (
    <>
      {!bannerHidden && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 9996,
            background: 'linear-gradient(90deg, #2563eb, #1d4ed8)',
            color: '#fff',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            flexWrap: 'wrap',
          }}
        >
          <Calendar size={16} />
          <span>
            <strong>{t('calendarConnect.bannerTitle')}</strong>
            {' — '}
            {t('calendarConnect.bannerDesc')}
          </span>
          <button
            onClick={handleConnect}
            disabled={connecting}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              color: '#fff',
              borderRadius: 4,
              padding: '4px 12px',
              cursor: connecting ? 'wait' : 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Calendar size={13} />
            {connecting ? t('common.loading') : t('calendarConnect.bannerCta')}
          </button>
          <button
            onClick={() => setBannerHidden(true)}
            title={t('calendarConnect.bannerDismiss')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.85)',
              cursor: 'pointer',
              padding: 4,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              background: 'var(--c-surface)',
              color: 'var(--c-text)',
              border: '1px solid var(--c-border)',
              borderRadius: 10,
              maxWidth: 480,
              width: '100%',
              padding: 24,
              boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <AlertTriangle size={22} color="#2563eb" />
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {t('calendarConnect.modalTitle')}
              </h2>
            </div>
            <p style={{ marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
              {t('calendarConnect.modalBody')}
            </p>
            <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 13, color: 'var(--c-text-muted)', lineHeight: 1.6 }}>
              <li>{t('calendarConnect.bullet1')}</li>
              <li>{t('calendarConnect.bullet2')}</li>
              <li>{t('calendarConnect.bullet3')}</li>
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
                {t('calendarConnect.modalLater')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConnect}
                disabled={connecting}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Calendar size={14} />
                {connecting ? t('common.loading') : t('calendarConnect.modalCta')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarConnectionBanner;
